'use client';

import { normalizeInternalPath } from './normalize-internal-path';

/**
 * Takes a href and an origin to determine if the link should be scoped as internal or public.
 * @param href The link to be scoped
 * @param origin The current origin of the served page. Example: 'https://internal.docs.unity3d.com' or 'https://docs.unity3d.com'
 * @returns The link without /internal/, also a full link to the public site if linking to a public page from an internal origin.
 */
export function createScopedLink(href: string, origin: string | undefined) {
  // If the href is already a full URL, return it as is
  if (/^https?:\/\//i.test(href)) return href;
  const prefixesToCheck = ['#', 'mailto:', 'tel:', 'sms:', 'ftp:', '//'];
  const lowerCaseHref = href.toLowerCase();
  if (prefixesToCheck.some((prefix) => lowerCaseHref.startsWith(prefix))) {
    return href;
  }

  const isCurrentlyInternal = origin?.includes('internal.') ?? false;

  const normalizedPath = normalizeInternalPath(href);
  const normalizedWithoutInternal = normalizedPath.replace(/\/internal\//, '/');
  const isPublicLinkFromInternal = isCurrentlyInternal && !/\/internal/.test(href);

  if (isPublicLinkFromInternal) {
    return origin?.replace('internal.', '') + normalizedWithoutInternal;
  }

  return normalizedWithoutInternal;
}
